import { useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';
import type { ComponentType } from 'react';

import { GateRedirect } from '@/components/GateRedirect';
import { userIdAtom } from '@/services/globalState';

/**
 * Whether the profile the route names is the signed-in reader's own, by id or by `me`.
 */
export function useIsOwnProfile(): boolean {
  const { id } = useLocalSearchParams();

  const currentUserId = useAtomValue(userIdAtom);

  const routeUserId = Array.isArray(id) ? id[0] : id;

  return !!currentUserId && (routeUserId === currentUserId.toString() || routeUserId === 'me');
}

/**
 * Lets only the profile's owner onto one of its edit screens, and sends anyone else to the
 * profile itself.
 *
 * The edit screens sit on the secure stack beside the profile, not in a stack of their own,
 * because only screens on one stack share a bar, and only a shared bar morphs the profile's
 * Edit into the edit screen's Save. A stack guard there cannot read the route's id, so the
 * check lives on the screen. It renders nothing until the reader's id is known, since the
 * secure layout signs a reader in first and the id arrives moments later.
 *
 * This is client-side reachability, not authorization. The API stays the enforcement
 * boundary for who may change a profile.
 */
export function withOwnProfile<Props extends object>(Screen: ComponentType<Props>) {
  function OwnProfileScreen(props: Props) {
    const { id } = useLocalSearchParams();

    const currentUserId = useAtomValue(userIdAtom);

    const isOwnProfile = useIsOwnProfile();

    if (!currentUserId) {
      return null;
    }

    if (!isOwnProfile) {
      return <GateRedirect href={`/profile/${Array.isArray(id) ? id[0] : id}`} />;
    }

    return <Screen {...props} />;
  }

  return OwnProfileScreen;
}
